import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  DownloadOutlined,
  IconWrench,
  QuestionCircleOutlined,
  ThunderboltOutlined,
} from '../components/icons'
import { Card } from '../components/ui/Card'
import { MaterialsAiModal } from './MaterialsAiModal'

interface ResourceCard {
  readonly key: string
  readonly icon: ReactNode
  readonly tint: 'blue' | 'teal' | 'purple' | 'orange'
  readonly title: string
  readonly desc: string
  /** Undefined for a stub card — UID-248 ships only "Material selection" as a
   * real entry point; the rest render as inert cards until their own tickets. */
  readonly onClick?: () => void
}

/**
 * Home "Help and resources" section (UID-248), matching the design mock's
 * card grid (`Fictiv Platform.dc.html:576-590`, `helpfulResources`). Only
 * "Material selection" is wired to a real entry point (the Materials.AI chat
 * modal) — FAQ, Explore our capabilities, and Try a sample part are stubbed
 * out per this ticket's scope and render as non-interactive cards.
 */
export function HelpAndResources() {
  const [matAiOpen, setMatAiOpen] = useState(false)

  const cards: readonly ResourceCard[] = [
    {
      key: 'faq',
      icon: <QuestionCircleOutlined aria-hidden="true" />,
      tint: 'blue',
      title: 'Frequently asked questions',
      desc: 'Answers about the platform and ordering process.',
    },
    {
      key: 'capabilities',
      icon: <IconWrench size={17} />,
      tint: 'teal',
      title: 'Explore our capabilities',
      desc: 'CNC machining, injection molding, 3D printing and more.',
    },
    {
      key: 'material-selection',
      icon: <ThunderboltOutlined aria-hidden="true" />,
      tint: 'purple',
      title: 'Material selection',
      desc: 'Find the right material with our AI-powered expert.',
      onClick: () => setMatAiOpen(true),
    },
    {
      key: 'sample-part',
      icon: <DownloadOutlined aria-hidden="true" />,
      tint: 'orange',
      title: 'Try a sample part',
      desc: 'Download a sample file to test process options.',
    },
  ]

  return (
    <section aria-labelledby="home-resources-heading">
      <h2 id="home-resources-heading" className="home-section-title">
        Help and resources
      </h2>
      <div className="home-resources-grid">
        {cards.map((card) =>
          card.onClick ? (
            <Card
              key={card.key}
              interactive
              className="home-resource-card"
              // biome-ignore lint/a11y/useSemanticElements: Card is the shared styled-div
              // primitive (ui.css `.card`) every other card composition in this app uses;
              // there is no nested interactive content here, so role="button" carries no
              // nested-interactive risk (unlike RfqListRow, which needs role="link" for
              // that reason).
              role="button"
              tabIndex={0}
              aria-label={card.title}
              onClick={card.onClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  card.onClick?.()
                }
              }}
            >
              <ResourceCardBody card={card} />
            </Card>
          ) : (
            <Card key={card.key} className="home-resource-card">
              <ResourceCardBody card={card} />
            </Card>
          ),
        )}
      </div>
      <MaterialsAiModal open={matAiOpen} onClose={() => setMatAiOpen(false)} />
    </section>
  )
}

function ResourceCardBody({ card }: { readonly card: ResourceCard }) {
  return (
    <>
      <span className={`home-resource-card-icon home-resource-card-icon-${card.tint}`}>
        {card.icon}
      </span>
      <div>
        <p className="home-resource-card-title">{card.title}</p>
        <p className="home-resource-card-desc">{card.desc}</p>
      </div>
    </>
  )
}
